<script setup>
import { useSmoothScroll } from "@composables/useSmoothScroll";

const footerBgs = [
    {
        src: "https://wishlisted.s3.amazonaws.com/images/backgrounds/footer-bg-1.svg",
        top: "-top-[120px]",
    },
    {
        src: "https://wishlisted.s3.amazonaws.com/images/backgrounds/footer-bg-2.svg",
        top: "-top-[64px]",
    },
];

const footerColumns = [
    {
        title: "Nav",
        col: "md:col-start-5 md:col-end-6",
        links: [
            { label: "What is it", href: "#hero" },
            { label: "Why Apply", href: "#why" },
            { label: "Follow Us", href: "#follow-us" },
        ],
    },
    {
        title: "Legal",
        col: "md:col-start-6 md:col-end-7",
        links: [
            { label: "Privacy Policy", href: "#privacy" },
            { label: "Terms and Conditions", href: "#terms" },
        ],
    },
];

const { handleAnchorClick } = useSmoothScroll();
</script>

<template>
    <footer
        class="relative flex w-full flex-col items-center gap-[120px] overflow-hidden px-[16px] pt-[48px] pb-[144px] md:items-start md:px-[48px] md:pt-[120px] md:pb-[400px]"
    >
        <!-- Background layers -->
        <div
            v-for="(bg, i) in footerBgs"
            :key="i"
            class="pointer-events-none absolute inset-0"
            :class="bg.top"
        >
            <img
                :src="bg.src"
                alt=""
                class="absolute block size-full max-w-none"
            />
        </div>

        <!-- Content grid -->
        <div class="container">
            <div
                class="relative z-10 flex w-full flex-col items-center gap-[72px] md:grid md:grid-cols-[repeat(6,minmax(0,1fr))] md:items-start md:gap-x-[16px] md:gap-y-[64px]"
            >
                <!-- Column 1: Logo + Copyright -->
                <div
                    class="flex flex-col items-center gap-[24px] text-center md:col-start-1 md:col-end-2 md:items-start md:text-left"
                >
                    <img
                        src="https://wishlisted.s3.amazonaws.com/images/logos/logo-footer.svg"
                        alt="Wishlisted"
                        class="h-[47.57px] w-[210.667px]"
                    />
                    <p
                        class="whitespace-nowrap font-obviously-narrow text-[16px] leading-[1.4] tracking-[0.16px] text-white/40"
                    >
                        &copy; {{ new Date().getFullYear() }} Wishlisted. All rights reserved.
                    </p>
                </div>

                <!-- Nav & Legal columns -->
                <div
                    v-for="column in footerColumns"
                    :key="column.title"
                    class="flex flex-col items-center gap-[24px] text-center md:items-start md:text-left"
                    :class="column.col"
                >
                    <h3
                        class="font-obviously font-bold text-[12px] uppercase leading-[1.4] tracking-[-0.12px] text-primary"
                    >
                        {{ column.title }}
                    </h3>
                    <ul class="flex flex-col gap-[24px]">
                        <li v-for="link in column.links" :key="link.href">
                            <a
                                :href="link.href"
                                class="group relative block h-[20px] overflow-hidden font-obviously-narrow text-[14px] leading-[20px] tracking-[0.14px] text-white"
                                @click="handleAnchorClick($event, link.href)"
                            >
                                <span
                                    class="block transition-transform duration-300 ease-out group-hover:-translate-y-full"
                                >
                                    {{ link.label }}
                                </span>
                                <span
                                    class="block transition-transform duration-300 ease-out group-hover:-translate-y-full"
                                    aria-hidden="true"
                                >
                                    {{ link.label }}
                                </span>
                            </a>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <!-- Giant "Wishlisted" decoration at bottom -->
        <div
            class="absolute bottom-0 left-1/2 h-[89px] w-full -translate-x-1/2 md:left-[-70px] md:h-[379px] md:w-full md:translate-x-0 md:scale-[1.1]"
        >
            <img
                src="https://wishlisted.s3.amazonaws.com/images/backgrounds/footer-decoration.svg"
                alt=""
                class="absolute block size-full max-w-none"
            />
        </div>
    </footer>
</template>
